import type { ItemFieldRow } from './item-field-reader';
import { layerReadView } from './layer-read-view';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';
import { phpJsonInteger, phpFloatToInteger, phpAdd, phpSubtract, phpMultiply, phpDivide } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';
import { scoreIntegerLayerWithResolvers, integerLayerTokensFromFragments } from './integer-layer-score';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value, key)
    ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const cell = (value: unknown): unknown => typeof value === 'string' ? value[0] : own(value, '0');
const validUuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const upperAscii = (value: string): string => value.replace(/[a-z]/g, ch => ch.toUpperCase());
const replace = (value: string, needle: string, replacement: string): string => value.split(needle).join(replacement);

export interface FormulaVariableMetadata {
    classes: readonly { readonly variable_name: string; readonly item_id: string }[];
    essences: { readonly body: string; readonly mind: string; readonly soul: string };
}
export interface FormulaVariableReaders {
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    layerEntries: (row: ItemFieldRow | null, name: string) => ItemFieldRow;
    itemField: (row: ItemFieldRow | null, name: string) => unknown;
    computedValue: (row: ItemFieldRow | null, bucket: string, key: string) => unknown;
}
export class FormulaDivisionByZeroError extends Error {}
export class FormulaArrayWriteError extends Error {}

/** Actual tiny arithmetic: first operator by +,-,*,/ priority, first two operands
 * only, with PHP intval on each operand before arithmetic. This is not an expression parser.
 */
export function resolveFormulaArithmeticValue(raw: unknown): unknown {
    if (arrayLike(raw)) throw new TypeError('Formula arithmetic requires a scalar string');
    const value = phpJsonString(raw);
    for (const operator of ['+', '-', '*', '/'] as const) {
        if (!value.includes(operator)) continue;
        const [left = '', right = ''] = value.split(operator), a = phpJsonInteger(left), b = phpJsonInteger(right);
        if (operator === '+') return phpAdd(a, b);
        if (operator === '-') return phpSubtract(a, b);
        if (operator === '*') return phpMultiply(a, b);
        if (b === 0n) throw new FormulaDivisionByZeroError('Division by zero');
        return phpDivide(a, b);
    }
    return raw;
}

/** Project only at the public compiler boundary; action integer consumers retain exact arithmetic. */
export function resolveFormulaArithmetic(raw: unknown): unknown {
    const value = resolveFormulaArithmeticValue(raw);
    return typeof value === 'bigint' ? Number(value) : value;
}

/** One inventory walk for compiler and action formulas. Actions name unreadable held inputs. */
export function readCasterGoldTotalInteger(row: ItemFieldRow | null, readers: FormulaVariableReaders, unavailable?: () => never): bigint {
    if (row === null) return 0n;
    if (!arrayLike(row)) throw new TypeError('Gold carrier requires a PHP array or null');
    const inventory = own(row, 'inventory');
    const root = arrayLike(inventory) ? inventory : readers.layerEntries(row, 'inventory');
    const seen = new Set<string>(); let total: PhpArithmetic = 0n;
    function walk(rows: unknown): void {
        if (!arrayLike(rows)) return;
        for (const entry of Object.values(rows)) {
            if (!arrayLike(entry)) continue;
            const id = phpJsonString(readInventoryField(entry, 'inventory_id') ?? '');
            const valid = id !== '' && validUuid(id);
            if (valid) { if (seen.has(id)) continue; seen.add(id); }
            if (valid) {
                const itemId = readers.itemField(entry, 'item_id');
                if (itemId == null) unavailable?.();
                if (upperAscii(phpJsonString(itemId ?? '')) === 'B0B6D994-03C5-D49A-C1FF-49C3C1723A4E') {
                    const rawQuantity = readInventoryField(entry, 'item_qty');
                    if (!isPhpNumeric(rawQuantity) || !Number.isFinite(Number(rawQuantity))) unavailable?.();
                    const quantity = phpJsonInteger(rawQuantity ?? 0);
                    total = phpAdd(total, quantity > 0n ? quantity : 0n);
                }
            }
            const children = own(entry, 'inventory_in_container');
            if (children != null && !arrayLike(children)) unavailable?.();
            if (phpJsonTruthy(children) && arrayLike(children)) walk(children);
        }
    }
    walk(root);
    if (typeof total !== 'bigint') throw new TypeError('Gold total overflowed its PHP integer return type');
    return total;
}
export function readCasterGoldTotal(row: ItemFieldRow | null, readers: FormulaVariableReaders): number {
    return Number(readCasterGoldTotalInteger(row, readers));
}
/** Exact catalog order and first matching slot; PHP breaks BOTH loops after
 * the first class replacement, even when another class tag remains in the formula.
 */
export function replaceFormulaClassLevels(row: ItemFieldRow, value: string, metadata: FormulaVariableMetadata): string {
    if (!arrayLike(row)) throw new TypeError('Class-level replacement requires a PHP array');
    if (value == null || arrayLike(value)) throw new TypeError('Class-level formula requires a scalar string');
    let variable = phpJsonString(value);
    const pc = own(row, 'player_characters');
    for (const entry of metadata.classes) {
        const tag = `[${entry.variable_name}_level]`;
        if (!variable.includes(tag)) continue;
        for (let slot = 1; slot <= 20; slot++) {
            if (own(pc, `class_${slot}`) !== entry.item_id || own(pc, `class_level_${slot}`) == null) continue;
            variable = replace(variable, tag, phpJsonString(own(pc, `class_level_${slot}`)));
            return variable;
        }
    }
    return variable;
}
/** Full discern_variable_tags flow over explicit pure readers. Values stay
 * source-derived; unresolved tags survive exactly as in PHP until tiny arithmetic.
 */
export function resolveFormulaVariables(raw: unknown, row: ItemFieldRow | null, x: unknown,
    metadata: FormulaVariableMetadata, readers: FormulaVariableReaders): string | number {
    let variable = phpJsonString(raw);
    if (x !== null && x !== '' && isPhpNumeric(x) && variable.includes('[x]')) variable = replace(variable, '[x]', String(phpJsonInteger(x)));
    const tags = ['body','mind','soul','spirit','luck','fortune','total_level','speed_walking','speed_climbing',
        'speed_swimming','speed_hovering','speed_flying','speed_burrowing','v_lost'];
    for (const tag of tags) {
        if (!variable.includes(`[${tag}]`)) continue;
        if (tag.startsWith('speed_') || ['body','mind','soul','spirit','fortune'].includes(tag)) {
            const resolved = readers.layerValue(row, tag);
            if (resolved !== null) variable = replace(variable, `[${tag}]`, phpJsonString(resolved));
            continue;
        }
        const rawCell = cell(own(row, tag));
        if (rawCell != null) {
            if (arrayLike(rawCell)) throw new TypeError('str_replace cannot use an array replacement with a string search');
            variable = replace(variable, `[${tag}]`, phpJsonString(rawCell));
        } else {
            const resolved = readers.layerValue(row, tag);
            if (resolved !== null) variable = replace(variable, `[${tag}]`, phpJsonString(resolved));
        }
    }
    for (const [legacy, canonical] of [['walking_speed','speed_walking'],['swimming_speed','speed_swimming']] as const) {
        if (!variable.includes(`[${legacy}]`)) continue;
        const resolved = readers.layerValue(row, canonical);
        if (resolved !== null) variable = replace(variable, `[${legacy}]`, phpJsonString(resolved));
    }
    if (variable.includes('[spellcasting_essence]')) {
        let modifier = readers.layerValue(row, 'spellcasting_essence_mod');
        if (modifier === null && readers.layerValue(row, 'spellcasting_essence') !== null) {
            const essence = readers.layerValue(row, 'spellcasting_essence');
            const name = Object.entries(metadata.essences).find(([, id]) => essence === id || essence === true)?.[0]
                ?? (essence === 'spellcasting_essence' ? essence : '');
            const legacy = cell(own(row, name)); if (legacy != null) modifier = legacy;
        }
        variable = replace(variable, '[spellcasting_essence]', String(phpJsonInteger(modifier ?? 0)));
    }
    if (variable.includes('[gold_total]')) variable = replace(variable, '[gold_total]', String(readCasterGoldTotalInteger(row, readers)));
    // [v_max] (2026-10-07, Warlock v13 "regain half your maximum Vitality"): the EFFECTIVE maximum — discern_variable_tags twin.
    if (variable.includes('[v_max]')) variable = replace(variable, '[v_max]', String(phpJsonInteger(readers.computedValue(row, 'vitality', 'effective_v_max') ?? 0)));
    if (variable.includes('[half_v_max]')) variable = replace(variable, '[half_v_max]', String(phpJsonInteger(readers.computedValue(row, 'vitality', 'effective_v_max') ?? 0) / 2n));
    if (variable === 'v_lost') variable = phpJsonString(readers.computedValue(row, 'vitality', 'v_lost') ?? 0);
    // The original by-reference typed helper rejects a null carrier here.
    variable = replaceFormulaClassLevels(row as ItemFieldRow, variable, metadata);
    return resolveFormulaArithmetic(variable) as string | number;
}

function layerArgument(value: unknown): string {
    if (value == null || arrayLike(value)) throw new TypeError('Integer layer name requires a scalar string');
    return phpJsonString(value);
}
/** Full return_int_score_item_layer_type_name: reads actual layer fragments,
 * resolves formula tags from the host, and uses ONLY raw root[0] for a multiplier
 * naming a different layer. The latter does not consult the computed bridge.
 */
export function scoreCarrierIntegerLayer(row: ItemFieldRow | null, rawLayer: string, plug: unknown, minZero: unknown,
    metadata: FormulaVariableMetadata, readers: FormulaVariableReaders): number {
    return Number(scoreCarrierIntegerLayerArithmetic(row,rawLayer,plug,minZero,metadata,readers));
}
/** floor returns a PHP double; the infinity-token early return alone is an integer. */
export function scoreCarrierIntegerLayerArithmetic(row: ItemFieldRow | null, rawLayer: string, plug: unknown, minZero: unknown,
    metadata: FormulaVariableMetadata, readers: FormulaVariableReaders): PhpArithmetic {
    const layer = layerArgument(rawLayer);
    if (row !== null && !arrayLike(row)) throw new TypeError('Integer score carrier requires a PHP array or null');
    const fragments = readers.layerEntries(row, layer);
    if (!arrayLike(fragments)) throw new TypeError('Layer entries must return a PHP array');
    if (integerLayerTokensFromFragments(Object.values(fragments)).length === 0
        && (arrayLike(plug) || (typeof plug === 'string' && !isPhpNumeric(plug)))) throw new TypeError('floor requires a numeric argument');
    // floor() sees the plug only when no scoreable tokens survive. Preserve that
    // lazy argument boundary instead of rejecting an unused malformed plug.
    const input = { layerName: layer, fragments: Object.values(fragments), variables: {}, plugValue: plug as number, minZero: phpJsonTruthy(minZero) };
    const result = scoreIntegerLayerWithResolvers(input, token => resolveFormulaVariables(token, row, null, metadata, readers), name => {
        const value = cell(own(row, name)); return isPhpNumeric(value) ? value : 0;
    });
    return integerLayerTokensFromFragments(Object.values(fragments)).some(token => /^(?:inf|infinity|\{inf\}|\{infinity\})$/i.test(token)) ? 99999n : result;
}
/** score_named_integer_layer_fragment: override only the fragment's canonical
 * slot in a host copy, dropping its root mirror but retaining other formula inputs.
 */
export function scoreNamedIntegerFragment(row: ItemFieldRow, fragments: ItemFieldRow, rawLayer: string,
    metadata: FormulaVariableMetadata, readers: FormulaVariableReaders): number {
    return Number(scoreNamedIntegerFragmentInteger(row, fragments, rawLayer, metadata, readers));
}
/** Internal composed arithmetic retains the integer until the public JSON boundary. */
export function scoreNamedIntegerFragmentInteger(row: ItemFieldRow, fragments: ItemFieldRow, rawLayer: string,
    metadata: FormulaVariableMetadata, readers: FormulaVariableReaders): bigint {
    if (!arrayLike(row) || !arrayLike(fragments)) throw new TypeError('Named integer fragment requires PHP arrays');
    const layer = layerArgument(rawLayer);
    if (Object.keys(fragments).length === 0) return 0n;
    const bucket = own(row, 'item_layer_types');
    if (bucket != null && bucket !== false && !arrayLike(bucket)) {
        if (typeof bucket === 'string') throw new TypeError('Cannot access offset of type string on string');
        throw new FormulaArrayWriteError('Cannot use scalar value as an array');
    }
    const probe = layerReadView(row, { item_layer_types: layerReadView(arrayLike(bucket) ? bucket : {}, { [layer]: Object.values(fragments) }) }, [layer]);
    return phpFloatToInteger(scoreCarrierIntegerLayer(probe, layer, 0, false, metadata, readers));
}
